import { Component, Input, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  AzureDevOpsProject,
  AzureWiki,
  AzureWikiPage
} from '../../../core/models/knowledge-azure-wiki.model';
import { extractErrorResponse, extractHttpStatus } from '../../../core/models/knowledge-error-response.model';
import { RESPONSE_STATUS } from '../../../core/constants/enums';
import { ToastService } from '../../../coding/refactor/services/toast.service';
import { AzureWikiService } from '../../../no-coding/knowledge/services/azure-wiki.service';

export type AzureWikiViewStep = 'projects' | 'wikis' | 'pages';

/** Fila visible del árbol de páginas (el árbol llega ya anidado, BR-33). */
export interface AzureWikiPageRow {
  page: AzureWikiPage;
  depth: number;
  hasChildren: boolean;
}

/**
 * Navegador de Azure DevOps Wiki (RF-002/RF-010): proyectos ADO → wikis →
 * árbol de páginas. El árbol COMPLETO llega en una sola llamada
 * (recursionLevel=Full, BR-33): expandir/colapsar es puramente en memoria,
 * sin peticiones por nodo. Una wiki sin páginas muestra estado vacío
 * explícito. "Importar" solo visible para Manager+ (BR-12).
 */
@Component({
  selector: 'app-azure-wiki-navigator',
  templateUrl: './azure-wiki-navigator.component.html',
  styleUrls: ['./azure-wiki-navigator.component.scss'],
  standalone: false
})
export class AzureWikiNavigatorComponent implements OnInit {
  @Input() projectId!: string;
  @Input() isManagerOrAbove = false;

  viewStep: AzureWikiViewStep = 'projects';
  loading = false;
  /** No hay conexión Azure DevOps configurada (424 CONNECTION_MISSING). */
  connectionMissing = false;
  /** La carga del paso actual falló: se muestra error, no el estado vacío. */
  stepLoadFailed = false;

  searchQuery = '';
  projects: AzureDevOpsProject[] = [];
  selectedProject: AzureDevOpsProject | null = null;
  wikis: AzureWiki[] = [];
  selectedWiki: AzureWiki | null = null;

  /** Árbol completo recibido del backend. */
  pageTree: AzureWikiPage[] = [];
  /** Filas visibles según nodos expandidos (derivadas en memoria). */
  visibleRows: AzureWikiPageRow[] = [];
  /** Todas las filas del árbol (para búsqueda en todos los niveles). */
  allPageRows: AzureWikiPageRow[] = [];
  private expanded = new Set<string>();
  selectedPagePaths = new Set<string>();

  constructor(
    private popoverController: PopoverController,
    private azureWikiService: AzureWikiService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  loadProjects(): void {
    this.loading = true;
    this.viewStep = 'projects';
    this.connectionMissing = false;
    this.searchQuery = '';
    this.azureWikiService.listProjects(this.projectId).subscribe({
      next: (list) => {
        this.projects = list || [];
        this.loading = false;
      },
      error: (err) => {
        this.projects = [];
        this.loading = false;
        this.connectionMissing =
          extractHttpStatus(err) === 424 &&
          extractErrorResponse(err)?.code === 'CONNECTION_MISSING';
        // Con conexión ausente el estado vacío del modal ya lo explica; el
        // toast solo aporta ruido.
        if (!this.connectionMissing) {
          this.showError(err);
        }
      }
    });
  }

  selectProject(project: AzureDevOpsProject): void {
    this.selectedProject = project;
    this.viewStep = 'wikis';
    this.searchQuery = '';
    this.loading = true;
    this.stepLoadFailed = false;
    this.azureWikiService.listWikis(this.projectId, project.name).subscribe({
      next: (list) => {
        this.wikis = list || [];
        this.loading = false;
      },
      error: (err) => {
        this.wikis = [];
        this.loading = false;
        this.stepLoadFailed = true;
        this.showError(err);
      }
    });
  }

  selectWiki(wiki: AzureWiki): void {
    this.selectedWiki = wiki;
    this.viewStep = 'pages';
    this.searchQuery = '';
    this.expanded.clear();
    this.selectedPagePaths.clear();
    this.loading = true;
    this.stepLoadFailed = false;
    this.azureWikiService.listPages(this.projectId, wiki.wikiId).subscribe({
      next: (tree) => {
        this.pageTree = tree || [];
        this.refreshVisibleRows();
        this.flattenAllPages();
        this.loading = false;
      },
      error: (err) => {
        this.pageTree = [];
        this.visibleRows = [];
        this.allPageRows = [];
        this.loading = false;
        this.stepLoadFailed = true;
        this.showError(err);
      }
    });
  }

  /** Aplana el árbol según los nodos expandidos — solo memoria, cero HTTP. */
  private refreshVisibleRows(): void {
    const rows: AzureWikiPageRow[] = [];
    const walk = (pages: AzureWikiPage[], depth: number) => {
      for (const page of pages) {
        const children = page.children || [];
        rows.push({ page, depth, hasChildren: children.length > 0 });
        if (children.length > 0 && this.expanded.has(page.pagePath)) {
          walk(children, depth + 1);
        }
      }
    };
    walk(this.pageTree, 0);
    this.visibleRows = rows;
  }

  private flattenAllPages(): void {
    const rows: AzureWikiPageRow[] = [];
    const walk = (pages: AzureWikiPage[], depth: number) => {
      for (const page of pages) {
        const children = page.children || [];
        rows.push({ page, depth, hasChildren: children.length > 0 });
        if (children.length > 0) walk(children, depth + 1);
      }
    };
    walk(this.pageTree, 0);
    this.allPageRows = rows;
  }

  isExpanded(row: AzureWikiPageRow): boolean {
    return this.expanded.has(row.page.pagePath);
  }

  toggleExpand(row: AzureWikiPageRow): void {
    if (!row.hasChildren) return;
    if (this.expanded.has(row.page.pagePath)) {
      this.expanded.delete(row.page.pagePath);
    } else {
      this.expanded.add(row.page.pagePath);
    }
    this.refreshVisibleRows();
  }

  toggleSelection(row: AzureWikiPageRow): void {
    if (this.selectedPagePaths.has(row.page.pagePath)) {
      this.selectedPagePaths.delete(row.page.pagePath);
    } else {
      this.selectedPagePaths.add(row.page.pagePath);
    }
  }

  isSelected(row: AzureWikiPageRow): boolean {
    return this.selectedPagePaths.has(row.page.pagePath);
  }

  get filteredProjects(): AzureDevOpsProject[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.projects;
    return this.projects.filter(p => p.name.toLowerCase().includes(q));
  }

  get filteredWikis(): AzureWiki[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.wikis;
    return this.wikis.filter(w => w.name.toLowerCase().includes(q));
  }

  get filteredVisibleRows(): AzureWikiPageRow[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.visibleRows;
    return this.allPageRows.filter(row =>
      row.page.pagePath.toLowerCase().includes(q)
    );
  }

  confirmImport(): void {
    if (!this.selectedWiki || this.selectedPagePaths.size === 0) return;
    const wikiId = this.selectedWiki.wikiId;
    const pagePaths = Array.from(this.selectedPagePaths);
    this.loading = true;
    const request$ = pagePaths.length === 1
      ? this.azureWikiService.importPage({ projectId: this.projectId, wikiId, pagePath: pagePaths[0] })
      : this.azureWikiService.importPagesBatch({ projectId: this.projectId, wikiId, pagePaths });
    request$.subscribe({
      next: (result) => {
        this.loading = false;
        const status = (result.failures?.length || result.capped)
          ? RESPONSE_STATUS.WARNING
          : RESPONSE_STATUS.OK;
        this.toastService.showToast(
          this.translateService.instant('KNOWLEDGE_FEATURE.AZURE_WIKI.TOASTS.IMPORT_RESULT', {
            processed: result.processed,
            requested: result.requested
          }),
          status
        );
        this.popoverController.dismiss({ result: true, imported: result });
      },
      error: (err) => {
        this.loading = false;
        this.showError(err, 'KNOWLEDGE_FEATURE.AZURE_WIKI.TOASTS.IMPORT_ERROR');
      }
    });
  }

  goBackToWikis(): void {
    this.viewStep = 'wikis';
    this.searchQuery = '';
    this.selectedWiki = null;
    this.pageTree = [];
    this.visibleRows = [];
    this.allPageRows = [];
    this.stepLoadFailed = false;
  }

  cancel(): void {
    this.popoverController.dismiss({ result: false });
  }

  formatDate(value: string | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
  }

  private showError(err: unknown, fallbackKey = 'KNOWLEDGE_FEATURE.AZURE_WIKI.TOASTS.LOAD_ERROR'): void {
    const status = extractHttpStatus(err);
    let message: string;
    if (status === 424) {
      // Un 424 distingue causas por `code`: credencial inválida/sin permisos
      // (PAT_INVALID) no es lo mismo que conexión ausente (CONNECTION_MISSING).
      const code = extractErrorResponse(err)?.code;
      message = this.translateService.instant(
        code === 'PAT_INVALID'
          ? 'KNOWLEDGE_FEATURE.AZURE_WIKI.TOASTS.PAT_INVALID'
          : 'KNOWLEDGE_FEATURE.AZURE_WIKI.TOASTS.NO_CONNECTION'
      );
    } else if (status === 429) {
      message = this.translateService.instant('KNOWLEDGE_FEATURE.AZURE_WIKI.TOASTS.RATE_LIMITED');
    } else {
      message = extractErrorResponse(err)?.message || this.translateService.instant(fallbackKey);
    }
    this.toastService.showToast(message, RESPONSE_STATUS.ERROR);
  }
}
